import * as React from 'react';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Paper from '@mui/material/Paper';
import { makeStyles } from '@mui/styles';

export type ListInfo = { listHeader: string; items: string[] };

type TransferListProps = {
  leftListInfo: ListInfo;
  rightListInfo: ListInfo;
  setLeftListInfo: React.Dispatch<React.SetStateAction<ListInfo>>;
  setRightListInfo: React.Dispatch<React.SetStateAction<ListInfo>>;
  isTransferMode?: boolean;
  hideTransferAllButtons?: boolean;
  isEmptyListAllowed?: boolean;
};

const useStyles = makeStyles(() => ({
  listsContainer: {
    width: '100%',
    height: 230,
    overflow: 'auto',
  },
  gridContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  listsButtons: { margin: '0.5em' },
  buttonsContainer: { alignItems: 'center' },
}));

function getDifference(listA: readonly string[], listB: readonly string[]): string[] {
  return listA.filter((value) => !listB?.includes(value));
}

function filterCheckedItems(selectedItems: readonly string[], sourceList: readonly string[]): string[] {
  return selectedItems.filter((value) => sourceList?.includes(value));
}

export default function TransferList(props: TransferListProps) {
  const {
    leftListInfo,
    rightListInfo,
    setLeftListInfo,
    setRightListInfo,
    isTransferMode = false,
    hideTransferAllButtons = false,
    isEmptyListAllowed = false,
  } = props;
  const [checked, setChecked] = React.useState<string[]>([]);
  const classes = useStyles();

  const leftChecked = filterCheckedItems(checked, leftListInfo.items);
  const rightChecked = filterCheckedItems(checked, rightListInfo.items);

  const handleToggle = (value: string) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleAllRight = () => {
    setRightListInfo((prev) => ({
      ...prev,
      items: Array.from(new Set([...prev.items, ...leftListInfo.items])),
    }));

    if (isTransferMode) setLeftListInfo((prev) => ({ ...prev, items: [] }));
  };

  const handleCheckedRight = () => {
    setRightListInfo((prev) => ({
      ...prev,
      items: Array.from(new Set([...prev.items, ...leftChecked])),
    }));

    if (isTransferMode) setLeftListInfo((prev) => ({ ...prev, items: getDifference(prev.items, leftChecked) }));
    setChecked(getDifference(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setLeftListInfo((prev) => ({
      ...prev,
      items: Array.from(new Set([...prev.items, ...rightChecked])),
    }));

    if (isTransferMode) setRightListInfo((prev) => ({ ...prev, items: getDifference(prev.items, rightChecked) }));
    setChecked(getDifference(checked, rightChecked));
  };

  const handleAllLeft = () => {
    setLeftListInfo((prev) => ({
      ...prev,
      items: Array.from(new Set([...prev.items, ...rightListInfo.items])),
    }));

    if (isTransferMode) setRightListInfo((prev) => ({ ...prev, items: [] }));
  };

  const customList = (listInfo: ListInfo) => (
    <Paper className={classes.listsContainer}>
      <List dense component="div" role="list">
        <ListSubheader component="div" id="list-subheader">
          {listInfo.listHeader || ''}
        </ListSubheader>

        {listInfo?.items?.map((value: string) => {
          const labelId = `transfer-list-item-${value}-label`;

          return (
            <ListItemButton
              key={value}
              role="listitem"
              onClick={handleToggle(value)}
              disabled={!isEmptyListAllowed && listInfo.items.length === 1}
            >
              <ListItemIcon>
                <Checkbox
                  checked={checked.includes(value)}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{
                    'aria-labelledby': labelId,
                  }}
                />
              </ListItemIcon>
              <ListItemText id={labelId} primary={value} />
            </ListItemButton>
          );
        })}
      </List>
    </Paper>
  );

  const showTransferAllOptions = () => (
    <>
      <Button
        className={classes.listsButtons}
        variant="outlined"
        size="small"
        onClick={handleAllRight}
        disabled={leftListInfo?.items?.length === 0}
        aria-label="move all right"
      >
        ≫
      </Button>
      <Button
        className={classes.listsButtons}
        variant="outlined"
        size="small"
        onClick={handleAllLeft}
        disabled={rightListInfo?.items?.length === 0}
        aria-label="move all left"
      >
        ≪
      </Button>
    </>
  );

  return (
    <Grid
      container
      spacing={2}
      className={classes.gridContainer}
    >
      <Grid>{customList(leftListInfo)}</Grid>
      <Grid>
        <Grid container direction="column" className={classes.buttonsContainer}>
          <Button
            className={classes.listsButtons}
            variant="outlined"
            size="small"
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0 || (!isEmptyListAllowed && leftChecked.length === leftListInfo.items.length)}
            aria-label="move selected right"
          >
            &gt;
          </Button>

          {!hideTransferAllButtons && isEmptyListAllowed && showTransferAllOptions()}

          <Button
            className={classes.listsButtons}
            variant="outlined"
            size="small"
            onClick={handleCheckedLeft}
            disabled={rightChecked.length === 0 || (!isEmptyListAllowed && rightChecked.length === rightListInfo.items.length)}
            aria-label="move selected left"
          >
            &lt;
          </Button>
        </Grid>
      </Grid>
      <Grid>{customList(rightListInfo)}</Grid>
    </Grid>
  );
}
